// DailySummaryTab.js
// This is the Daily Summary Tab for the Sampling Event Form

import React, { useState, useEffect, useRef } from "react";
import styles from "./DailySummaryTab.module.css";
// Adding the appStore
import { useAppStore } from "../../../../store/appStore";
import { EmployeeSelectionModal } from "./components/DailySummaryEmployeeSelectionModal";
import { AddDailySummaryModal } from "./components/AddDailySummaryModal";
import { CompleteSamplingEventModal } from "./components/CompleteSamplingEventModal";
import { DailySummarySignatureSection } from "./components/DailySummarySignatureSection";
import { AddWellModal } from "./components/AddWellModal";
import { UpdateSamplingGroupsModal } from "./components/UpdateSamplingGroupsModal";
// Utility file
import { formatDateForDisplay } from "../../samplingEventUtils";

// Basic Info Container - displays event information (read-only)
const BasicInfoContainer = ({ samplingEventData, actions, samplingEventMode }) => {
  const dsActions = actions?.samplingEventFormActions?.dailySummaries;
  const handleCompleteSamplingEvent = () =>
    dsActions?.openCompleteEventModal?.();

  const canEdit =
    samplingEventMode !== "view" && !samplingEventData?.completed;

  // Actions dropdown (Add Well / Update Sampling Groups)
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef(null);

  useEffect(() => {
    if (!isActionsMenuOpen) return;
    const handleClickOutside = (event) => {
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(event.target)
      ) {
        setIsActionsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isActionsMenuOpen]);

  const handleSelectAddWell = () => {
    setIsActionsMenuOpen(false);
    dsActions?.openAddWellModal?.();
  };

  const handleSelectUpdateGroups = () => {
    setIsActionsMenuOpen(false);
    dsActions?.openUpdateGroupsModal?.();
  };

  return (
    <div className={styles.dailySummaryBasicInfoContainer}>
      <div className={styles.dailySummaryBasicInfoRow}>
        <div className={styles.dailySummaryBasicInfoInput}>
          <label>Event Name:</label>
          <input
            type="text"
            value={samplingEventData?.eventName || ""}
            disabled
          />
        </div>
        <div className={styles.dailySummaryBasicInfoInput}>
          <label>Sampling Site:</label>
          <input
            type="text"
            value={samplingEventData?.wellSite || ""}
            disabled
          />
        </div>
      </div>

      <div className={styles.dailySummaryBasicInfoRow}>
        <div className={styles.dailySummaryBasicInfoInput}>
          <label>Start Date:</label>
          <input
            type="text"
            value={samplingEventData?.startDate || ""}
            disabled
          />
        </div>
        <div className={styles.dailySummaryBasicInfoInput}>
          <label>End Date:</label>
          <input
            type="text"
            value={samplingEventData?.endDate || ""}
            disabled
          />
        </div>
      </div>

      <div className={styles.dailySummaryBasicInfoRow}>
        <div className={styles.dailySummaryBasicInfoInput}>
          <label>Project Number:</label>
          <input
            type="text"
            value={samplingEventData?.projectNumber || ""}
            disabled
          />
        </div>
        <div className={styles.dailySummaryBasicInfoInput}>
          <label>Phase ID:</label>
          <input
            type="text"
            value={samplingEventData?.phaseId || ""}
            disabled
          />
        </div>
        {samplingEventData?.taskId && (
          <div className={styles.dailySummaryBasicInfoInput}>
            <label>Task ID:</label>
            <input
              type="text"
              value={samplingEventData?.taskId || ""}
              disabled
            />
          </div>
        )}
      </div>
      <div className={styles.dailySummaryBasicInfoRow}>
        <div className={styles.dailySummaryBasicInfoInput}>
          <label>Complete Sampling Event:</label>
          <button
            onClick={handleCompleteSamplingEvent}
            className={styles.addDailySummaryButton}
          >
            Complete Sampling Event
          </button>
        </div>
        {canEdit && (
          <div className={styles.dailySummaryBasicInfoInput}>
            <label>Actions:</label>
            <div
              className={styles.actionsDropdownContainer}
              ref={actionsMenuRef}
            >
              <button
                onClick={() => setIsActionsMenuOpen((open) => !open)}
                className={styles.addDailySummaryButton}
              >
                Actions {isActionsMenuOpen ? "▲" : "▼"}
              </button>
              {isActionsMenuOpen && (
                <div className={styles.actionsMenu}>
                  <button
                    className={styles.actionsMenuItem}
                    onClick={handleSelectAddWell}
                  >
                    Add Well
                  </button>
                  <button
                    className={styles.actionsMenuItem}
                    onClick={handleSelectUpdateGroups}
                  >
                    Update Sampling Groups
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Daily Summary Card Component - individual expandable card for each day
const DailySummaryCard = ({
  summaryData,
  date,
  isExpanded,
  onToggle,
  allEmployees,
  onUpdateSummary,
  completedInspectionWells,
  completedSamplingWells,
  employeeModalOpen,
  employeeSelectedEmployees,
  onEmployeeOpen,
  onEmployeeClose,
  onEmployeeSelectionChange,
  tabProps,
  actions,
  samplingEventMode,
}) => {
  // Handle text area changes
  const handleUpdateNotes = (e, sendPatch = false) => {
    actions.setNotesDraft(summaryData.id, e.target.value);
    // This will trigger a re-render of the component
    if (sendPatch) {
      actions.updateDailySummary(
        summaryData.id,
        { notes: e.target.value },
        tabProps.emitPatch
      );
    }
  };

  const handleUpdateMaintenance = (e, sendPatch = false) => {
    actions.setMaintenanceDraft(summaryData.id, e.target.value);
    // This will trigger a re-render of the component
    if (sendPatch) {
      actions.updateDailySummary(
        summaryData.id,
        { maintenance: e.target.value },
        tabProps.emitPatch
      );
    }
  };

  // Handle checkbox changes & other direct updates (signature)
  const handleDirectUpdate = (field, functionName, value) => {
    const response = actions.updateCheckbox(
      summaryData.id,
      functionName,
      value
    );
    if (response.ok) {
      actions.updateDailySummary(
        summaryData.id,
        { [field]: value },
        tabProps.emitPatch
      );
    }
  };

  // Handle selecting employees for the daily summary, and setting up the emitpatch
  const handleOnSelectEmployees = (selected) => {
    if (!selected || selected.length === 0) return;
    const updatedEmployees = [
      ...(summaryData.employees || []),
      ...selected.map((employee) => ({
        employee_id: employee.employee_id,
        employee_name: employee.name,
      })),
    ];
    actions.addEmployeesToDailySummary(summaryData.id, updatedEmployees);
    actions.updateDailySummary(
      summaryData.id,
      { employees: updatedEmployees },
      tabProps.emitPatch
    );
    onEmployeeClose();
  };

  // Handle employee removal
  const handleRemoveEmployee = (employee) => {
    actions.removeEmployeeFromDailySummary(summaryData.id, employee);
    actions.updateDailySummary(
      summaryData.id,
      { employees: summaryData.employees },
      tabProps.emitPatch
    );
  };

  return (
    <div className={styles.dailySummaryCard}>
      <div className={styles.dailySummaryCardHeader} onClick={onToggle}>
        <h3>Daily Summary - {formatDateForDisplay(summaryData.date)}</h3>
        <span className={styles.expandIcon}>{isExpanded ? "▼" : "▶"}</span>
      </div>

      {isExpanded && (
        <div className={styles.dailySummaryCardContent}>
          {/* Section 1: Personnel */}
          <div className={styles.summarySection}>
            <h4>Personnel On Site</h4>
            {summaryData.employees && summaryData.employees.length > 0 ? (
              <div className={styles.employeesList}>
                <table className={styles.employeeTable}>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryData.employees.map((employee) => (
                      <tr key={employee.employee_id}>
                        <td>{employee.employee_name}</td>
                        <td>
                          <button
                            className={styles.removeButton}
                            onClick={() => handleRemoveEmployee(employee)}
                            disabled={samplingEventMode === "view"}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles.noData}>
                No personnel assigned for this day
              </div>
            )}

            <button
              className={styles.addButton}
              onClick={onEmployeeOpen}
              disabled={samplingEventMode === "view"}
            >
              Add Personnel
            </button>

            <EmployeeSelectionModal
              isOpen={!!employeeModalOpen}
              onClose={onEmployeeClose}
              onSelectEmployee={handleOnSelectEmployees}
              allEmployees={allEmployees}
              existingEmployees={summaryData.employees || []}
              selectedEmployees={employeeSelectedEmployees}
              onSelectionChange={onEmployeeSelectionChange}
            />
          </div>

          {/* Section 2: Completed Wells Table */}
          <div className={styles.summarySection}>
            <h4>Inspections Completed</h4>
            <div className={styles.wellsTable}>
              <table>
                <thead>
                  <tr>
                    <th>Well ID</th>
                    <th>TD Measured</th>
                    <th>DTW Measured</th>
                    <th>Inspected By</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Completed inspections, if inspectionCompleted is true and inspectionCompletedDate is today */}
                  {completedInspectionWells.length > 0 ? (
                    completedInspectionWells
                      .filter(
                        (well) =>
                          well.inspectionCompletedDate === summaryData.date
                      )
                      .map((well, index) => (
                        <tr key={index}>
                          <td>{well.wellId}</td>
                          <td>
                            {well.measuredTotalDepthNm ? "False" : "True"}
                          </td>
                          <td>
                            {well.measuredDepthToWaterNm ? "False" : "True"}
                          </td>
                          <td>
                            {allEmployees.find(
                              (employee) =>
                                employee.employee_id ===
                                well.inspectionCompletedBy
                            )?.name || "N/A"}
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="3" className={styles.noData}>
                        No Inspected Wells for this day
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3: Completed Wells Table */}
          <div className={styles.summarySection}>
            <h4>Wells Sampled</h4>
            <div className={styles.wellsTable}>
              <table>
                <thead>
                  <tr>
                    <th>Well ID</th>
                    <th>QC Samples</th>
                    <th>Sample Time</th>
                    <th>Sampled By</th>
                  </tr>
                </thead>
                <tbody>
                  {completedSamplingWells.length > 0 ? (
                    completedSamplingWells
                      .filter(
                        (well) =>
                          well.samplingCompletedDate === summaryData.date
                      )
                      .map((well, index) => (
                        <tr key={index}>
                          <td>{well.wellId}</td>
                          <td>{well.qcSamples}</td>
                          <td>{well.sampleTime}</td>
                          <td>
                            {well.employeeSignatures
                              .map((signature) => signature.employeeName)
                              .join(", ") || "N/A"}
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="4" className={styles.noData}>
                        No Wells Sampled for this day
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 4: Notes */}
          <div className={styles.summarySection}>
            <h4>Notes</h4>
            <textarea
              className={styles.textArea}
              value={summaryData.notes || ""}
              onChange={(e) => handleUpdateNotes(e, false)}
              onBlur={(e) => handleUpdateNotes(e, true)}
              placeholder="Enter notes for this day..."
              disabled={samplingEventMode === "view"}
            />
          </div>

          {/* Section 5: Maintenance Needs */}
          <div className={styles.summarySection}>
            <h4>Maintenance Needs</h4>
            <textarea
              className={styles.textArea}
              value={summaryData.maintenance || ""}
              onChange={(e) => handleUpdateMaintenance(e, false)}
              onBlur={(e) => handleUpdateMaintenance(e, true)}
              placeholder="Enter maintenance needs for this day..."
              disabled={samplingEventMode === "view"}
            />
          </div>

          {/* Section 6: Checklist */}
          <div className={styles.summarySection}>
            <h4>Daily Checklist</h4>
            <div className={styles.checklistItem}>
              <input
                type="checkbox"
                id={`forms-${summaryData.id}`}
                checked={summaryData.formsFilledOut || false}
                onChange={(e) =>
                  handleDirectUpdate(
                    "formsFilledOut",
                    "setFormsFilledOut",
                    e.target.checked
                  )
                }
                disabled={samplingEventMode === "view"}
              />
              <label htmlFor={`forms-${summaryData.id}`}>
                Forms and logs filled out and complete
              </label>
            </div>

            <div className={styles.checklistItem}>
              <input
                type="checkbox"
                id={`coolers-${summaryData.id}`}
                checked={summaryData.coolersPackedShipped || false}
                onChange={(e) =>
                  handleDirectUpdate(
                    "coolersPackedShipped",
                    "setCoolersPackedShipped",
                    e.target.checked
                  )
                }
                disabled={samplingEventMode === "view"}
              />
              <label htmlFor={`coolers-${summaryData.id}`}>
                Coolers properly packed and shipped
              </label>
            </div>

            <div className={styles.checklistItem}>
              <input
                type="checkbox"
                id={`site-${summaryData.id}`}
                checked={summaryData.siteSecure || false}
                onChange={(e) =>
                  handleDirectUpdate(
                    "siteSecure",
                    "setSiteSecure",
                    e.target.checked
                  )
                }
                disabled={samplingEventMode === "view"}
              />
              <label htmlFor={`site-${summaryData.id}`}>Site Secure</label>
            </div>

            <div className={styles.signatureArea}>
              <DailySummarySignatureSection
                summaryData={summaryData}
                allEmployees={allEmployees}
                samplingEventMode={samplingEventMode}
                ui={tabProps?.dailySummaryUI}
                actions={actions}
                emitPatch={tabProps?.emitPatch}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main DailySummaryTab Component
export const DailySummaryTab = ({ samplingEventData, tabProps, actions }) => {
  // Notification store
  const showNotification = useAppStore(
    (s) => s.notificationActions.showNotification
  );
  const dailySummaries = samplingEventData?.dailySummaries || [];
  const expandedCardId = tabProps?.dailySummaryUI?.expandedCardId || null;
  const dsActions = actions?.samplingEventFormActions?.dailySummaries;
  const allEmployees = tabProps?.allEmployees || [];
  const samplingEventMode = tabProps?.samplingEventMode || "";

  // Keep track of samplingEventWells to update completed inspections and completed sampling
  const samplingEventWells = samplingEventData?.samplingEventWells || [];

  // Track wells with completed inspections/sampling
  const [completedInspectionWells, setCompletedInspectionWells] = useState([]);
  const [completedSamplingWells, setCompletedSamplingWells] = useState([]);

  // Get emitPatch from tabProps (provided by useSamplingEventAutosave hook)
  const emitPatch = tabProps?.emitPatch;

  useEffect(() => {
    // Filter wells where inspection is completed and get their IDs
    const inspectionWells = samplingEventWells
      .filter((well) => well.inspectionCompleted === true)
      .map((well) => well);

    // Filter wells where sampling is completed and get their IDs
    const samplingWells = samplingEventWells
      .filter((well) => well.samplingCompleted === true)
      .map((well) => well);

    setCompletedInspectionWells(inspectionWells);
    setCompletedSamplingWells(samplingWells);
  }, [samplingEventWells, samplingEventData]);

  const isAddModalOpen = !!tabProps?.dailySummaryUI?.isAddModalOpen;
  const isCompleteModalOpen =
    !!tabProps?.dailySummaryUI?.completeEventModal?.isOpen;
  const isAddWellModalOpen =
    !!tabProps?.dailySummaryUI?.addWellModal?.isOpen;
  const isUpdateGroupsModalOpen =
    !!tabProps?.dailySummaryUI?.updateGroupsModal?.isOpen;
  const wellsActions = actions?.samplingEventFormActions?.wells;
  const groupsActions = actions?.samplingEventFormActions?.groups;

  // Submit handler for the Add Well modal: delegates the optimistic add +
  // patch emit to the wells slice and only resolves once the action returns.
  const handleAddWellSubmit = async ({ wells, samplingGroupId }) => {
    if (!wellsActions?.addSamplingEventWells) return;
    const result = await wellsActions.addSamplingEventWells({
      wells,
      samplingGroupId,
      emitPatch,
    });
    if (!result?.ok) {
      showNotification({
        type: "error",
        title: "Error Adding Well",
        message: result?.error || "Unable to add the selected wells.",
      });
    } else if (result.added > 0) {
      showNotification({
        type: "success",
        title: "Well Added",
        message:
          result.added === 1
            ? "1 well was added to the sampling event."
            : `${result.added} wells were added to the sampling event.`,
      });
    }
  };

  // Prepare existing dates for the modal to prevent duplicates
  // Ensure dates are in "YYYY-MM-DD" format if not already
  const existingDates = dailySummaries.map((summary) => {
    if (summary.date instanceof Date) {
      return summary.date.toISOString().split("T")[0];
    }
    return summary.date; // Assuming it's already YYYY-MM-DD string if not Date object
  });

  // Handle closing and opening the daily summary modal.
  const handleOpenAddModal = () => dsActions.openAddModal();
  const handleCloseAddModal = () => dsActions.closeAddModal();

  // Handle adding a new daily summary
  const handleAddSummarySubmit = (selectedDate) => {
    dsActions.createDailySummaryForDate(selectedDate, emitPatch);
    dsActions.closeAddModal();
  };

  // console.log("samplingEventData", samplingEventData);

  return (
    <div className={styles.dailySummaryTabContainer}>
      {/* Basic Info Container */}
      <BasicInfoContainer
        samplingEventData={samplingEventData}
        actions={actions}
        samplingEventMode={samplingEventMode}
      />

      {/* Daily Summary Cards */}
      <div className={styles.dailySummaryCardsContainer}>
        <h2 className={styles.sectionTitle}>Daily Summary</h2>

        {/* Add New Daily Summary Button */}
        {samplingEventMode !== "view" && (
          <button
            onClick={handleOpenAddModal}
            className={styles.addDailySummaryButton}
          >
            Add New Daily Summary
          </button>
        )}

        <p className={styles.sectionDescription}>
          Click on a card to expand and view/edit the daily summary details
        </p>

        {dailySummaries.length > 0 ? (
          dailySummaries.map((summary) => {
            const summaryForDate = summary;

            return (
              <DailySummaryCard
                key={summaryForDate.id}
                summaryData={summaryForDate}
                date={summaryForDate.date}
                isExpanded={expandedCardId === summaryForDate.id}
                onToggle={() => dsActions.setExpandedCard(summaryForDate.id)}
                allEmployees={allEmployees}
                completedInspectionWells={completedInspectionWells}
                completedSamplingWells={completedSamplingWells}
                setNotesDraft={dsActions.setNotesDraft}
                setMaintenanceDraft={dsActions.setMaintenanceDraft}
                employeeModalOpen={
                  !!tabProps?.dailySummaryUI?.employeeModal?.isOpen &&
                  tabProps?.dailySummaryUI?.employeeModal?.forSummaryId ===
                    summaryForDate.id
                }
                employeeSelectedEmployees={
                  tabProps?.dailySummaryUI?.employeeModal?.selectedEmployees ||
                  []
                }
                onEmployeeOpen={() =>
                  dsActions.openEmployeeModal(summaryForDate.id)
                }
                onEmployeeClose={() => dsActions.closeEmployeeModal()}
                onEmployeeSelectionChange={(emps) =>
                  dsActions.setEmployeeModalSelection(emps)
                }
                tabProps={tabProps}
                actions={dsActions}
                samplingEventMode={samplingEventMode}
              />
            );
          })
        ) : (
          <div className={styles.noDataMessage}>
            No date range available. Please set start and end dates for this
            sampling event.
          </div>
        )}
      </div>

      <AddDailySummaryModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        onSubmit={handleAddSummarySubmit}
        existingDates={existingDates}
      />

      <AddWellModal
        isOpen={isAddWellModalOpen}
        onClose={() => dsActions?.closeAddWellModal?.()}
        onSubmit={handleAddWellSubmit}
        samplingEventData={samplingEventData}
      />

      <UpdateSamplingGroupsModal
        isOpen={isUpdateGroupsModalOpen}
        onClose={() => dsActions?.closeUpdateGroupsModal?.()}
        samplingEventData={samplingEventData}
        allEmployees={allEmployees}
        groupsActions={groupsActions}
        emitPatch={emitPatch}
      />

      <CompleteSamplingEventModal
        isOpen={isCompleteModalOpen}
        onClose={() => dsActions?.closeCompleteEventModal?.()}
        onConfirm={async (employeeId) => {
          const response = await dsActions?.confirmCompleteEvent?.(
            employeeId,
            emitPatch
          );
          console.log("response", response);
          if (response.ok) {
            showNotification({
              type: "success",
              title: "Sampling Event Completed",
              message: "This event is now in view-only mode.",
            });
            // Close the complete event modal
            dsActions?.closeCompleteEventModal?.();
          } else {
            showNotification({
              type: "error",
              title: "Error Completing Sampling Event",
              message: response.error,
            });
          }
        }}
        samplingEventData={samplingEventData}
        allEmployees={tabProps?.allEmployees || []}
        user={tabProps?.user}
      />
    </div>
  );
};
